import { Css } from "~generated/css";
import { useTestIds } from "~utils";

import { useViewport } from "src/contexts";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { FlowStep } from "src/pages/configurator/stepTemplates/FlowSteps";
import { InsideMapsIFrame } from "./InsideMapsIFrame";

export function InsideMapsLocations({ hidePreviews }: { hidePreviews: boolean }) {
  const { checkoutConfig, currentSubStep } = useConfiguratorContext();
  const { isMobile } = useViewport();
  // Interior personalization is ALWAYS first, and we don't show it as a preview
  const [_interiorPersonalization, ...roomSubSteps] = checkoutConfig?.interiorSubSteps ?? [];

  // Hide substep container while config is loading or if on mobile.
  if (!checkoutConfig || isMobile) return null;

  return (
    <div css={Css.bgTaupe.bt.bcGray200.df.jcsa.gap2.$}>
      <div css={Css.df.gap1.oa.p2.$}>
        {roomSubSteps.map(({ displayName, location, id, stepNumber }) => {
          return (
            <LocationCard
              key={displayName}
              name={displayName}
              isActive={stepNumber === currentSubStep}
              subStepNumber={stepNumber}
              stepId={id}
              roomLocationId={location?.location.id}
              hidePreviews={hidePreviews}
            />
          );
        })}
      </div>
    </div>
  );
}

type LocationCardProps = {
  name: string;
  stepId: string;
  isActive: boolean;
  subStepNumber: number;
  roomLocationId: string | undefined;
  hidePreviews?: boolean;
};

function LocationCard(props: LocationCardProps) {
  const { name, stepId, isActive, subStepNumber, roomLocationId, hidePreviews } = props;
  const { goToStep, insideMaps } = useConfiguratorContext();
  const isAdditionalPersonalization = stepId === "additional-personalization";
  const tid = useTestIds(props, "location-card");

  return (
    <button
      onClick={() => goToStep(FlowStep.Interior, `${subStepNumber}`)}
      css={{
        // `oh` ensures child elements have their border radius clipped to the parent's border radius
        ...Css.relative.mwPx(128).cursorPointer.bshBasic.br4.oh.add("transition", "0.2s").$,
      }}
      {...tid}
    >
      {/* Highlight Bar */}
      {isActive && <div css={Css.absolute.z1.left0.right0.top0.hPx(4).bgBrightYellow.$} />}
      {insideMaps.insideMapsProjectId && !isAdditionalPersonalization && (
        <div css={Css.hPx(72).$}>
          <InsideMapsIFrame
            roomLocationId={roomLocationId}
            isPreview={true}
            iframeId={`${name} Preview`}
            hidePreviews={hidePreviews}
          />
        </div>
      )}
      {isAdditionalPersonalization && (
        <div css={Css.hPx(72).oh.bgLightBlue.df.jcc.aic.$}>
          <img
            css={Css.add("borderRadius", "4px 4px 0 0").hPx(52).wPx(75).$}
            src="/personalization-placeholder.svg"
            alt="Additional Personalization"
          ></img>
        </div>
      )}
      {/* Text Container */}
      <div css={Css.bgWhite.p1.$}>
        <span css={Css.add("transition", "0.2s").t12.gray800.t12.wsnw.$}>
          {LOCATION_CARD_ABBREVIATIONS[name] ?? name}
        </span>
      </div>
    </button>
  );
}

const LOCATION_CARD_ABBREVIATIONS: Record<string, string> = {
  "Additional Personalization": "Add. Personalization",
};
